import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import MainNav from '@/components/layout/AppNavbar';
import WorldCard from '@/components/ui/WorldCard';
import Pagination from '@/components/ui/Pagination';
import Axios from '@/shared/api/axios';
import Loading from '@/components/ui/Loading';
import StatusModal from '@/components/ui/StatusModal';
import { useDebouncedSearchInput } from '@/hooks/useDebouncedSearchInput';

const WorldsPage = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const idolId = useSelector((state) => state.user.idolId);

  const [worlds, setWorlds] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // API 非成功狀態碼統一由 StatusModal 顯示（含攔截器附加的 HTTP_xxx）
  const statusModalRef = useRef(null);
  const [currentStatusCode, setCurrentStatusCode] = useState('');

  // 從 URL 讀取參數
  const currentPage = parseInt(searchParams.get('page') || '1');
  const keyword = searchParams.get('keyword') || '';

  // 分頁邏輯
  const itemsPerPage = 12;
  const totalPages = Math.ceil(total / itemsPerPage);

  // 更新 URL 參數；navigateOptions 原樣傳給 setSearchParams（例如 { replace: true }）
  const updateParams = (key, value, navigateOptions) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    newParams.set('page', '1');
    setSearchParams(newParams, navigateOptions);
  };

  // 搜尋框：輸入先存本地，停頓後才寫回網址（replace，不留每個字的瀏覽紀錄），見 useDebouncedSearchInput
  const [keywordInput, setKeywordInput, keywordCompositionProps] = useDebouncedSearchInput(
    keyword,
    (value) => updateParams('keyword', value, { replace: true })
  );

  const handlePageChange = (page) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', page);
    // 換頁捲動交給 Pagination 的 scrollMode="window" 處理，避免跟 ScrollRestoration 的瞬間置頂互搶
    setSearchParams(newParams, { preventScrollReset: true });
  };

  // 取得世界列表
  useEffect(() => {
    // 搜尋條件或頁碼改變後，前一次請求的回應較晚回來時丟棄，避免蓋掉新條件的結果
    let ignore = false;

    const fetchWorlds = async () => {
      if (!idolId) {
        setWorlds([]);
        setTotal(0);
        // 前一次被丟棄的請求不會關 loading，這裡要自己關
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const payload = {
          idol_id: idolId,
          limit: itemsPerPage,
          page: currentPage,
          keyword: keyword || ''
        };

        const res = await Axios.post('/world/get-list', payload);
        if (ignore) return;
        const data = res.data;

        if (data.status_code === 'SYSTEM_1000') {
          const rawData = Array.isArray(data.data) ? data.data : [];

          // 直接取 world_media[0].url，沒有就交給 WorldCard 自己用 placeholder
          const mapped = rawData.map((item) => {
            const firstMedia =
              Array.isArray(item.world_media) && item.world_media.length > 0
                ? item.world_media[0]
                : null;

            return {
              ...item,
              world_cover_url: firstMedia?.url || undefined
            };
          });

          setWorlds(mapped);
          setTotal(typeof data.total === 'number' ? data.total : mapped.length);
        } else {
          setCurrentStatusCode(data.status_code);
          statusModalRef.current?.show();
          setWorlds([]);
          setTotal(0);
        }
      } catch (error) {
        if (ignore) return;
        // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
        if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
          setCurrentStatusCode(error.httpErrorCode);
          statusModalRef.current?.show();
        } else if (!error.httpErrorCode) {
          console.error('Get world list error:', error);
        }
        setWorlds([]);
        setTotal(0);
      } finally {
        // 被丟棄的請求不關 loading：較新的請求還在途中
        if (!ignore) setIsLoading(false);
      }
    };

    fetchWorlds();
    return () => {
      ignore = true;
    };
  }, [idolId, currentPage, keyword]);

  return (
    <>
      <MainNav />
      <Container fluid="lg" className="nav-padding">
        <Row className="pt-4">
          <Col xs={12}>
            <h3 className="mb-4">{t('worldview')}</h3>
          </Col>
        </Row>

        <Row>
          <Col xs={12}>
            {/* 搜尋框 */}
            <div className="d-flex justify-content-end mb-4">
              <div className="position-relative flex-grow-1 flex-md-grow-0" style={{ maxWidth: '300px' }}>
                <FontAwesomeIcon
                  icon={["fas", "search"]}
                  className="position-absolute"
                  style={{ left: '15px', top: '50%', transform: 'translateY(-50%)', zIndex: 10 }}
                />
                <Form.Control
                  type="search"
                  placeholder={t('search')}
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  {...keywordCompositionProps}
                  className="ps-5 py-2 rounded-5 border-0"
                />
              </div>
            </div>

            {/* WorldCard Grid */}
            {worlds.length > 0 ? (
              <div className="cards-grid mb-4">
                {worlds.map(world => (
                  <WorldCard
                    key={world.world_id}
                    world={world}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-5 text-muted">{t('no_data')}</div>
            )}

            {/* 分頁 */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-center">
                <Pagination
                  total={total}
                  limit={itemsPerPage}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                  scrollMode="window"
                />
              </div>
            )}
          </Col>
        </Row>
      </Container>

      {isLoading && <Loading />}
      <StatusModal ref={statusModalRef} statusCode={currentStatusCode} />
    </>
  );
};

export default WorldsPage;
